import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiAlertCircle } from 'react-icons/fi';
import { membersApi } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from './auth/AuthProvider';
import { auth } from '../firebase';


export default function MemberDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State for member data and form handling
  const [memberData, setMemberData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Get the member ID from the route - either from memberId (new route) or from the URL
  const { id: teamId, memberId } = params;
  const currentMemberId = memberId || params.memberId;

  // Log authentication token for debugging (as requested in the issue)
  useEffect(() => {
    const logAuthToken = async () => {
      if (user) {
        try {
          const token = await auth.currentUser?.getIdToken();
          console.log('🔐 User authentication token for debugging:', token);
        } catch (error) {
          console.error('❌ Error getting auth token for logging:', error);
        }
      } else {
        console.log('⚠️ No authenticated user found');
      }
    };
    
    logAuthToken();
  }, [user]);

  // Fetch member data when component mounts
  useEffect(() => {
    const fetchMemberData = async () => {
      if (!currentMemberId) {
        setError('Member ID is required');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await membersApi.fetchMemberById(currentMemberId);
        setMemberData(data.member);
        setOriginalData(data.member);
      } catch (err) {
        console.error('Error fetching member data:', err);
        setError('Failed to load member data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [currentMemberId]);

  const handleBack = () => {
    if (teamId) {
      navigate(`/teams/${teamId}`);
    } else {
      // If no teamId (from new route), go back in history
      navigate(-1);
    }
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setMemberData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear any previous save messages when editing
    setSaveError(null);
    setSaveSuccess(false);
  };

  // Check if data has been modified
  const hasChanges = () => {
    if (!memberData || !originalData) return false;
    
    // Only check editable fields
    const editableFields = [
      'id', 'hs_team_id', 'hs_owner_id', 'hs_user_id', 'hs_portal_id', 
      'member_active', 'percent', 'weight', 'first_name', 'last_name'
    ];
    
    return editableFields.some(field => 
      memberData[field] !== originalData[field]
    );
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!memberData) return;
    
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Only send editable fields
      const updateData = {
        id: memberData.id,
        hs_team_id: memberData.hs_team_id,
        hs_owner_id: memberData.hs_owner_id,
        hs_user_id: memberData.hs_user_id,
        hs_portal_id: memberData.hs_portal_id,
        member_active: memberData.member_active,
        percent: memberData.percent,
        weight: memberData.weight,
        first_name: memberData.first_name,
        last_name: memberData.last_name
      };

      await membersApi.updateMember(currentMemberId, updateData);
      
      // Update original data to reflect saved state
      setOriginalData(memberData);
      setSaveSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving member data:', err);
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Field component for consistent styling
  const FormField = ({ label, field, type = 'text', readonly = false }) => {
    const value = memberData?.[field] ?? '';
    
    if (readonly) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-3 border-b border-gray-100">
          <dt className="text-sm font-medium text-gray-900">{label}</dt>
          <dd className="text-sm text-gray-700 md:col-span-2">
            {field.includes('date') ? formatDate(value) : (value || 'N/A')}
          </dd>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-3 border-b border-gray-100">
        <dt className="text-sm font-medium text-gray-900">{label}</dt>
        <dd className="md:col-span-2">
          {type === 'select' ? (
            <select
              value={value}
              onChange={(e) => handleFieldChange(field, parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          ) : type === 'number' ? (
            <input
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value ? parseFloat(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          )}
        </dd>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <button
              onClick={handleBack}
              className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition duration-200"
              title="Back to Team Details"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Member Details</h1>
              <p className="text-gray-600">
                {teamId && `Team ID: ${teamId} • `}Member ID: {currentMemberId}
              </p>
            </div>
          </div>
          
          {/* Save Changes Button */}
          {memberData && hasChanges() && (
            <button
              onClick={handleSaveChanges}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSave className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
        
        {/* Success/Error Messages */}
        {saveSuccess && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">Changes saved successfully!</p>
          </div>
        )}
        
        {saveError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <FiAlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{saveError}</p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading member data...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-8 mb-4">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        ) : memberData ? (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                {memberData.first_name} {memberData.last_name}
              </h2>
              <p className="text-sm text-gray-600">
                Edit member information below. Fields marked as read-only cannot be modified.
              </p>
            </div>
            
            <dl className="divide-y divide-gray-100">
              <FormField label="Member ID" field="id" readonly />
              <FormField label="HubSpot Team ID" field="hs_team_id" />
              <FormField label="HubSpot Owner ID" field="hs_owner_id" />
              <FormField label="HubSpot User ID" field="hs_user_id" />
              <FormField label="HubSpot Portal ID" field="hs_portal_id" />
              <FormField label="Active Member" field="member_active" type="select" />
              <FormField label="Last Assignment" field="last_assignment" readonly />
              <FormField label="Date Created" field="date_created" readonly />
              <FormField label="Last Updated" field="last_updated" readonly />
              <FormField label="Last Updated By" field="last_updated_by" readonly />
              <FormField label="Percent" field="percent" type="number" />
              <FormField label="Weight" field="weight" type="number" />
              <FormField label="First Name" field="first_name" />
              <FormField label="Last Name" field="last_name" />
            </dl>
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No member data found</h3>
            <p className="text-gray-600">
              Unable to load member information for ID: {currentMemberId}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}